import React, { useRef, useState, useCallback } from "react";
import { Cropper, ReactCropperElement } from "react-cropper";
import "cropperjs/dist/cropper.css";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { compressCanvasToJpeg } from "../utils/functions/compressImage";

interface ImageCropperDialogProps {
  visible: boolean;
  image: string; // data URL of the loaded file
  cropWidth: number;
  cropHeight: number;
  onConfirm: (croppedBlob: Blob) => void;
  onCancel: () => void;
  /** When true, skip minimum dimension validation */
  skipDimensionCheck?: boolean;
  maxSizeKb?: number;
}

export const ImageCropperDialog: React.FC<ImageCropperDialogProps> = ({
  visible,
  image,
  cropWidth,
  cropHeight,
  onConfirm,
  onCancel,
  skipDimensionCheck = false,
  maxSizeKb,
}) => {
  const cropperRef = useRef<ReactCropperElement>(null);
  const [processing, setProcessing] = useState(false);
  const [dimensionError, setDimensionError] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [processingError, setProcessingError] = useState<string>();

  const aspect = cropWidth / cropHeight;

  const onReady = useCallback(() => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;

    const imageData = cropper.getImageData();
    const { naturalWidth, naturalHeight } = imageData;

    if (!skipDimensionCheck && (naturalWidth < cropWidth || naturalHeight < cropHeight)) {
      setDimensionError(
        `La imagen debe tener al menos ${cropWidth}x${cropHeight}px. ` +
        `La imagen cargada es de ${naturalWidth}x${naturalHeight}px.`
      );
    } else {
      setDimensionError(null);
    }
    setImageLoaded(true);
    // `skipDimensionCheck` se lee adentro, asi que va en las deps. Hoy no cambia el
    // comportamiento (es un prop constante por instancia del dialogo), pero sin el, si algun dia
    // alguien lo hace dinamico, el chequeo de dimensiones se quedaria con el valor viejo.
  }, [cropWidth, cropHeight, skipDimensionCheck]);

  const handleConfirm = async () => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper || dimensionError) return;

    setProcessing(true);
    setProcessingError(undefined);
    try {
      const canvas = cropper.getCroppedCanvas({
        width: cropWidth,
        height: cropHeight,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: "high",
        ...(maxSizeKb ? { fillColor: "#ffffff" } : {}),
      });

      if (maxSizeKb) {
        onConfirm(await compressCanvasToJpeg(canvas, maxSizeKb));
        setProcessing(false);
        return;
      }

      canvas.toBlob(
        (blob) => {
          if (blob) {
            onConfirm(blob);
          } else {
            console.error("Canvas toBlob failed");
          }
          setProcessing(false);
        },
        "image/jpeg",
        0.92
      );
    } catch (err) {
      setProcessingError(err instanceof Error ? err.message : "No se pudo procesar la imagen.");
      console.error("Error cropping image:", err);
      setProcessing(false);
    }
  };

  const handleHide = () => {
    setDimensionError(null);
    setImageLoaded(false);
    setProcessingError(undefined);
    onCancel();
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancelar"
        icon="pi pi-times"
        severity="secondary"
        onClick={handleHide}
        disabled={processing}
      />
      <Button
        label="Confirmar recorte"
        icon="pi pi-check"
        severity="success"
        onClick={handleConfirm}
        disabled={processing || !!dimensionError || !imageLoaded}
        loading={processing}
      />
    </div>
  );

  return (
    <Dialog
      header={`Recortar imagen (${cropWidth}x${cropHeight}px)`}
      visible={visible}
      onHide={handleHide}
      style={{ width: "90vw", maxWidth: "900px" }}
      footer={footer}
      modal
      closable={!processing}
      draggable={false}
    >
      {processingError && <p role="alert" className="p-error">{processingError}</p>}
      {dimensionError ? (
        <div className="flex flex-column align-items-center gap-3 p-4">
          <i
            className="pi pi-exclamation-triangle"
            style={{ fontSize: "3rem", color: "var(--red-500)" }}
          />
          <p className="text-center text-red-500 font-semibold m-0">
            {dimensionError}
          </p>
          <Button
            label="Seleccionar otra imagen"
            icon="pi pi-image"
            onClick={handleHide}
          />
        </div>
      ) : (
        <div>
          <Cropper
            ref={cropperRef}
            src={image}
            style={{ height: "60vh", maxHeight: "500px", width: "100%" }}
            aspectRatio={aspect}
            viewMode={1}
            minCropBoxWidth={100}
            minCropBoxHeight={Math.round(100 / aspect)}
            guides={true}
            center={true}
            highlight={true}
            background={true}
            responsive={true}
            autoCropArea={0.9}
            checkOrientation={false}
            ready={onReady}
            dragMode="move"
            cropBoxMovable={true}
            cropBoxResizable={true}
            toggleDragModeOnDblclick={false}
          />
          {!imageLoaded && (
            <div
              className="flex align-items-center justify-content-center"
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(0,0,0,0.5)",
              }}
            >
              <ProgressSpinner />
            </div>
          )}
          <p className="text-sm text-500 mt-3 mb-0">
            Arrastrá las esquinas del recorte para redimensionarlo (aspect ratio fijo {cropWidth}:{cropHeight}).
            Arrastrá la imagen para moverla. Usá la rueda del mouse para hacer zoom.
            El resultado será exactamente {cropWidth}x{cropHeight} píxeles.
          </p>
        </div>
      )}
    </Dialog>
  );
};
